import React from 'react'
import { Alert, Button, Flex, Form, Input, InputNumber, Select } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { colorOptions } from '../../../../../utils/antd.helper'
import { COLORS } from '../../../../../utils/colors.helper'
import { isNumber } from 'lodash'
import ConceptualLabelSelect from '../../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import Access from '../../../components/access/Access'
import { AssignmentPermissions, Forbiddances } from '../../../../../constants/assignment-permissions'
import Text from 'antd/es/typography/Text'
import { useAssignmentAccess } from '../../../components/access/Access'
import MarkdownRenderer from '../../../../../components/common/markdown-renderer'

const CategoryForm = ({ 
    onSubmit, 
    initialValues, 
    error, 
    loading, 
    actionName = 'Submit',
    extraActions = [],
}) => {
    const hasEditPermission = useAssignmentAccess([AssignmentPermissions.CAN_EDIT])

    return (
        <Form name='edit-question-form' autoComplete='off' onFinish={values => onSubmit(values)} requiredMark={false} layout='vertical'>
            <Form.Item
                name='name'
                label='Name'
                rules={[{ required: true, message: 'Please, enter name' }]}
                initialValue={initialValues?.name}
            >
                <Access
                    permissions={[AssignmentPermissions.CAN_EDIT]}
                    fallback={<Text>{initialValues?.name || 'No name provided'}</Text>}
                >
                    <Input placeholder='Enter name' />
                </Access>
            </Form.Item>
            <Form.Item
                label='Feedback'
                name='feedback'
                initialValue={initialValues?.feedback}
                rules={[{ required: true, message: 'Please, enter feedback' }]}
            >
                <Access
                    permissions={[AssignmentPermissions.CAN_EDIT]}
                    skipForbiddances={[
                        Forbiddances.IS_PROCESSING,
                        Forbiddances.IS_VERSION_NOT_LATEST,
                        Forbiddances.IS_VERSION_PUBLISHED,
                        Forbiddances.VERSION_HAS_MBAS,
                        Forbiddances.IS_MBA,
                    ]}
                    fallback={<Text>{initialValues?.feedback || 'No feedback provided'}</Text>}
                >
                    <TextArea rows={4} placeholder='Enter feedback' />
                </Access>
            </Form.Item>
            <Form.Item
                label='Description'
                name='description'
                initialValue={initialValues?.description}
                rules={[{ required: true, message: 'Please, enter criteria for assigning to this category' }]}
                tooltip='Markdown is supported: # Heading, **Bold text**, - List item, 1. Numbered item'
            >
                <Access
                    permissions={[AssignmentPermissions.CAN_EDIT]}
                    fallback={
                        <MarkdownRenderer content={initialValues?.description || 'No description provided'} />
                    }
                >
                    <TextArea rows={4} placeholder='Enter criteria for assigning to this category' />
                </Access>
            </Form.Item>
            <Flex justify='space-between'>
                <Form.Item
                    label='Color'
                    name='colorId'
                    initialValue={isNumber(initialValues?.colorId) ? initialValues?.colorId % COLORS.length : 0}
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={<Text>{colorOptions.find(opt => opt.value === initialValues?.colorId)?.label || 'No color selected'}</Text>}
                    >
                        <Select 
                            options={colorOptions} 
                            popupMatchSelectWidth={false} 
                            getPopupContainer={triggerNode => triggerNode.parentNode} 
                            showSearch 
                            optionFilterProp='label' 
                        />
                    </Access>
                </Form.Item>
                <Form.Item
                    label='Grade'
                    name='grade'
                    rules={[
                        { required: true, message: 'Please, enter grade' },
                        {
                            type: 'number',
                            message: 'Please, input a number',
                        },
                    ]}
                    initialValue={initialValues?.grade}
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={<Text>{initialValues?.grade || 'No grade set'}</Text>}
                    >
                        <InputNumber />
                    </Access>
                </Form.Item>
            </Flex>
            <Form.Item
                name='conceptualLabels'
                label='Conceptual labels'
                initialValue={initialValues?.conceptualLabels}
                tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
            >
                {!hasEditPermission ? (
                    <Text>
                        {initialValues?.conceptualLabels?.length
                            ? initialValues.conceptualLabels.map(label => label.name).join(', ')
                            : 'No conceptual labels selected'}
                    </Text>
                ) : (
                    <ConceptualLabelSelect disabled={!hasEditPermission} />
                )}
            </Form.Item>
            {error && <Alert message={error} type='error' showIcon closable />}
            <Form.Item>
                <Access
                    permissions={[AssignmentPermissions.CAN_EDIT]}
                    skipForbiddances={[
                        Forbiddances.IS_PROCESSING,
                        Forbiddances.IS_VERSION_NOT_LATEST,
                        Forbiddances.IS_VERSION_PUBLISHED,
                        Forbiddances.VERSION_HAS_MBAS,
                        Forbiddances.IS_MBA,
                    ]}
                >
                    <Flex justify='end' gap={8}>
                        {extraActions.map((Action, index) => (
                            <React.Fragment key={index}>
                                {Action}
                            </React.Fragment>
                        ))}
                        <Button type='primary' htmlType='submit' loading={loading}>
                            {actionName}
                        </Button>
                    </Flex>
                </Access>
            </Form.Item>
        </Form>
    )
}

export default CategoryForm
